import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { HiOutlineSearch, HiX } from "react-icons/hi";
import Api from "../../../service/http";
import {
  formatTickerApiLimitMessage,
  isTickerApiLimitResponse,
} from "../../../utils/tickerApiLimit";
import TickerLimitFloat from "./TickerLimitFloat";

const SelectTicker = ({
  selected,
  setSelected,
  onChange = false,
  /** When true (default), X clears committed ticker. Time & Sales passes false to keep the symbol loaded. */
  clearResetsSelection = true,
}) => {
  const { theme } = useSelector((state) => state.theme);
  const [query, setQuery] = useState(
    selected == null || selected === "" || selected === "all" ? "" : String(selected)
  );
  const [tickerList, setTickerList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const blurTimerRef = useRef(null);
  const atWeeklyLimitRef = useRef(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const syncDropdownPosition = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  };

  const openDropdown = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setInputFocused(true);
    setShowDropdown(true);
    syncDropdownPosition();
  };

  const handleInputFocus = () => {
    openDropdown();
    if (selected != null && selected !== "" && selected !== "all") {
      setQuery(String(selected));
    }
  };

  const handleInputMouseDown = (e) => {
    if (e.target.closest("button")) return;
    openDropdown();
  };

  const handleInputBlur = () => {
    blurTimerRef.current = setTimeout(() => {
      setInputFocused(false);
      setShowDropdown(false);
      setDropdownRect(null);
      const display =
        selected == null || selected === "" || selected === "all"
          ? ""
          : String(selected);
      setQuery(display);
    }, 150);
  };

  const closeDropdown = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setShowDropdown(false);
    setInputFocused(false);
    setDropdownRect(null);
    inputRef.current?.blur();
  };

  const clearSearchDraft = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setTickerList([]);
    setLimitError(null);
    atWeeklyLimitRef.current = false;
    setLoading(false);
    setQuery("");
    if (clearResetsSelection) {
      setSelected(null);
    }
    openDropdown();
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (onChange) {
      setSelected(query);
    }
  }, [query]);

  useEffect(() => {
    if (inputFocused) return;
    const display =
      selected == null || selected === "" || selected === "all"
        ? ""
        : String(selected);
    setQuery(display);
  }, [selected, inputFocused]);

  const commitSelection = (item) => {
    if (!item?.value) return;
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setSelected(item.value);
    setQuery(item.label);
    setShowDropdown(false);
    setInputFocused(false);
    setDropdownRect(null);
  };

  const commitFromQuery = () => {
    const q = String(query ?? "").trim();
    if (!q) return;
    const exact = tickerList.find(
      (item) => String(item.label).toLowerCase() === q.toLowerCase()
    );
    if (exact) {
      commitSelection(exact);
      return;
    }
    if (tickerList.length === 1) {
      commitSelection(tickerList[0]);
    }
  };

  const getTickers = async (query) => {
    const showLoader = !atWeeklyLimitRef.current;

    try {
      if (showLoader) {
        setLoading(true);
        setTickerList([]);
      }
      const res = await Api.get("/app/marketdata/ticker", {
        params: { q: query },
      });

      if (isTickerApiLimitResponse(res?.data, res?.status)) {
        atWeeklyLimitRef.current = true;
        setLimitError(formatTickerApiLimitMessage(res.data));
        setLoading(false);
        return;
      }

      if (res?.data?.status === "error") {
        setLimitError({
          headline: res.data.message || "Could not search tickers.",
          detail: "Please try again in a moment.",
        });
        setLoading(false);
        return;
      }

      atWeeklyLimitRef.current = false;
      setLimitError(null);

      let newData = [...(res?.data?.data || [])].sort(
        (a, b) => a.label.length - b.label.length
      );

      const exactData = newData.find(
        (item) => item.label.toLowerCase() === query.toLowerCase()
      );
      if (exactData) {
        newData = [exactData, ...newData.slice(0, 9)];
      }

      // Remove duplicates based on label
      const uniqueLabels = new Set();
      const filteredData = newData.filter((item) => {
        if (uniqueLabels.has(item.label.toLowerCase())) {
          return false;
        }
        uniqueLabels.add(item.label.toLowerCase());
        return true;
      });

      setTickerList(filteredData || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tickers", err);
      setTickerList([]);
      const status = err?.response?.status;
      const payload = err?.response?.data;
      if (isTickerApiLimitResponse(payload, status)) {
        atWeeklyLimitRef.current = true;
        setLimitError(formatTickerApiLimitMessage(payload));
      } else {
        setLimitError({
          headline: payload?.message || "Ticker search failed.",
          detail: "Check your connection and try again.",
        });
      }
      setLoading(false);
    }
  };

  // Debounced fetch on query change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (query && query.trim()) {
        getTickers(query);
      } else {
        setTickerList([]);
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;
    syncDropdownPosition();
    const onReposition = () => syncDropdownPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [showDropdown, query, tickerList.length]);

  // Close dropdown on outside click (keep DOM focus in sync with React state)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        const portal = document.getElementById("select-ticker-dropdown-portal");
        if (portal?.contains(e.target)) return;
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="group relative w-full min-w-0 max-w-full sm:max-w-[280px]">
      <div className="relative flex items-center">
        <div className={`absolute left-3 transition-colors duration-200 ${
          inputFocused ? 'text-blue-500' : 'text-slate-400'
        }`}>
          <HiOutlineSearch size={18} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onMouseDown={handleInputMouseDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitFromQuery();
            }
          }}
          placeholder="Search Ticker (e.g. AAPL)"
          className={`w-full pl-10 pr-10 py-2.5 text-sm font-medium transition-all duration-200 border rounded-xl outline-none shadow-sm ${
            theme === 'dark'
              ? 'bg-slate-800/50 border-slate-700 text-slate-100 placeholder-slate-500 focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10'
              : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10'
          }`}
        />

        {/* Clear Button */}
        {query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSearchDraft}
            className="absolute right-3 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            title="Clear search"
          >
            <HiX size={14} />
          </button>
        )}
      </div>

      {limitError && inputFocused ? (
        <TickerLimitFloat
          message={limitError}
          theme={theme}
          className="absolute left-1/2 top-[calc(100%+0.35rem)] -translate-x-1/2 sm:left-auto sm:right-0 sm:translate-x-0"
        />
      ) : null}

      {showDropdown && !limitError && dropdownRect
        ? createPortal(
            <div
              id="select-ticker-dropdown-portal"
              className={`fixed z-[200] overflow-hidden border rounded-xl shadow-2xl backdrop-blur-md transition-all duration-200 animate-in fade-in slide-in-from-top-2 ${
                theme === "dark"
                  ? "bg-slate-800/95 border-slate-700"
                  : "bg-white/95 border-slate-200"
              }`}
              style={{
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
              }}
            >
              <ul className="max-h-60 overflow-y-auto py-1">
                {tickerList.length > 0 ? (
                  tickerList.map((opt) => (
                    <li
                      key={opt.value}
                      className={`px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors flex items-center justify-between ${
                        theme === "dark"
                          ? "text-slate-200 hover:bg-blue-600/20"
                          : "text-slate-700 hover:bg-blue-50"
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commitSelection(opt)}
                    >
                      <span>{opt.label}</span>
                      {selected === opt.value && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      )}
                    </li>
                  ))
                ) : loading && !limitError ? (
                  <li className="px-4 py-3 text-xs font-medium text-slate-400 text-center flex items-center justify-center gap-2">
                    <div className="w-3 h-3 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                    Searching...
                  </li>
                ) : query ? (
                  <li className="px-4 py-3 text-xs font-medium text-slate-400 text-center">
                    No tickers found
                  </li>
                ) : (
                  <li className="px-4 py-3 text-xs font-medium text-slate-400 text-center">
                    Type a symbol to start
                  </li>
                )}
              </ul>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SelectTicker;
